import CallMergeRoundedIcon from '@mui/icons-material/CallMergeRounded';
import { Chip } from '@mui/material';
import type { ChipProps } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { ConversationStatusLike } from './types';

export interface ConversationStatusChipProps {
  /** Always `currentEpisode?.status ?? status` at the call site — design §11.1's one rule. */
  status: ConversationStatusLike;
  size?: ChipProps['size'];
}

const COLOR: Record<ConversationStatusLike, ChipProps['color']> = {
  WAITING_OPERATOR: 'warning',
  OPERATOR: 'info',
  OPEN: 'default',
  CLOSED: 'default',
  MERGED: 'default',
};

const VARIANT: Record<ConversationStatusLike, ChipProps['variant']> = {
  WAITING_OPERATOR: 'filled',
  OPERATOR: 'filled',
  OPEN: 'outlined',
  CLOSED: 'filled',
  MERGED: 'outlined',
};

/**
 * The status chip shared by the inbox row, the conversation header and Customer 360 (design §2, §11.1).
 * Reads either the episode status or the thread's mirror `status` — the caller resolves which.
 */
export function ConversationStatusChip({ status, size = 'small' }: ConversationStatusChipProps) {
  const { t } = useTranslation('conversations');
  const label = t(`status.${statusKey(status)}`, status);
  return (
    <Chip
      size={size}
      color={COLOR[status] ?? 'default'}
      variant={VARIANT[status] ?? 'outlined'}
      icon={status === 'MERGED' ? <CallMergeRoundedIcon fontSize="inherit" /> : undefined}
      label={label}
    />
  );
}

function statusKey(status: ConversationStatusLike): string {
  switch (status) {
    case 'WAITING_OPERATOR':
      return 'waiting';
    case 'OPERATOR':
      return 'withOperator';
    case 'OPEN':
      return 'open';
    case 'CLOSED':
      return 'closed';
    case 'MERGED':
      return 'merged';
    default:
      return status;
  }
}
